import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useCmsContent } from '../hooks/useCmsContent';
import { useLanguage } from '../contexts/LanguageContext';
import { parseHighlight } from '../utils/richText';
import { testimonialsFallback } from '../data/testimonialsFallback';
import { getTestimonials } from '../services/testimonialsService';
import { TestimonialCard } from '../components/testimonials/TestimonialCard';

export const Testimonials = () => {
  const cms = useCmsContent('testimonials');
  const { t, language } = useLanguage();
  const [testimonials, setTestimonials] = useState(testimonialsFallback);

  const heroBadge = cms.get('hero', 'badge', t('testimonials.hero.badge'));
  const heroTitle = cms.get('hero', 'title', t('testimonials.hero.title'));
  const heroBody = cms.get('hero', 'body', t('testimonials.hero.body'));
  const ctaTitle = cms.get('cta', 'title', t('testimonials.ctaSection.title'));
  const ctaBody = cms.get('cta', 'body', t('testimonials.ctaSection.body'));
  const ctaPrimary = cms.get('cta', 'cta_primary', t('testimonials.ctaSection.primary'));
  const ctaSecondary = cms.get('cta', 'cta_secondary', t('testimonials.ctaSection.secondary'));

  useEffect(() => {
    getTestimonials(language)
      .then((data) => {
        if (data?.length) {
          setTestimonials(data);
        }
      })
      .catch(() => setTestimonials(testimonialsFallback));
  }, [language]);

  return (
    <main className="pt-24" data-testid="testimonials-page">
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="badge-orange mb-4 inline-block">{heroBadge}</span>
            <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
              {parseHighlight(heroTitle)}
            </h1>
            <p className="text-lg text-[#1A2E6E]/70 leading-relaxed">{heroBody}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id || testimonial.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <TestimonialCard testimonial={testimonial} index={index} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-paper-lines">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 md:p-12 rounded-3xl text-center max-w-3xl mx-auto"
          >
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {parseHighlight(ctaTitle)}
            </h2>
            <p className="text-[#1A2E6E]/70 mb-8">{ctaBody}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/level-test" className="btn-primary inline-flex items-center gap-2">
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link to="/book-trial" className="btn-secondary">
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
